import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, X, FileSignature, Pencil, Trash2, Eye, EyeOff } from "lucide-react";

const KINDS = [
  { value: "consent", label: "Consent form" },
  { value: "roi", label: "Release of Information" },
  { value: "intake_packet", label: "Intake packet" },
  { value: "letter", label: "Letter" },
  { value: "other", label: "Other" },
];

const PLACEHOLDERS = [
  { token: "{{first_name}}", note: "Care Seeker first name" },
  { token: "{{last_name}}", note: "Care Seeker last name" },
  { token: "{{preferred_name}}", note: "Preferred name (falls back to first)" },
  { token: "{{display_name}}", note: "Preferred name + last name" },
  { token: "{{display_id}}", note: "CSK####" },
  { token: "{{phone}}", note: "Phone" },
  { token: "{{email}}", note: "Email" },
  { token: "{{dob}}", note: "Date of birth" },
  { token: "{{age}}", note: "Computed age" },
  { token: "{{address}}", note: "City + County" },
  { token: "{{city}}", note: "City" },
  { token: "{{county}}", note: "County" },
  { token: "{{today_long}}", note: "Today, e.g. 'May 17, 2026'" },
  { token: "{{staff_name}}", note: "Current logged-in staff name" },
  { token: "{{staff_email}}", note: "Current staff email" },
  { token: "{{staff_title}}", note: "Current staff title/role" },
  { token: "{{org_name}}", note: "HarborWell, Inc." },
];

export default function DocumentTemplates() {
  const { can } = useAuth();
  const isAdmin = can("training.create"); // reuse — same admin/ED perm tier
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = isAdmin ? "/document-templates/all" : "/document-templates";
      const { data } = await api.get(url);
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try {
      await api.delete(`/document-templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="document-templates-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="hw-eyebrow mb-2">Admin</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">Document templates</h1>
          <p className="text-sm text-[#334155] mt-1 max-w-2xl">
            Reusable consent forms, ROIs, and letters that staff can mailmerge to a Care Seeker with one click. Use <span className="font-mono text-[11px] bg-[#F9F8F6] px-1 rounded">{"{{placeholder}}"}</span> tokens — see the helper card below.
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            data-testid="add-document-template-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554]"
          >
            <Plus className="w-4 h-4" /> New template
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#1E3A8A]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-12 text-center" data-testid="dt-empty">
          <FileSignature className="w-10 h-10 text-[#1E3A8A] mx-auto mb-3" strokeWidth={1.5} />
          <h3 className="font-display text-lg font-semibold text-[#0F172A]">No templates yet</h3>
          {isAdmin && (
            <button
              onClick={() => { setEditing(null); setShowForm(true); }}
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> Create first template
            </button>
          )}
        </div>
      ) : (
        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="dt-list">
          {items.map((t) => (
            <li
              key={t.id}
              data-testid={`dt-card-${t.id}`}
              className={`bg-white rounded-2xl border p-4 hw-card-hover ${t.active ? "border-[#E5E5E0]" : "border-[#FCD34D] bg-[#FFFBEB]"}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#DBEAFE] text-[#1E40AF] flex items-center justify-center flex-shrink-0">
                  <FileSignature className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider text-[#1E3A8A] font-semibold">
                      {(KINDS.find((k) => k.value === t.kind) || { label: t.kind }).label}
                    </span>
                    {!t.active && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] font-semibold inline-flex items-center gap-1">
                        <EyeOff className="w-3 h-3" /> Inactive
                      </span>
                    )}
                  </div>
                  <h3 className="font-medium text-[#0F172A] mt-0.5 leading-snug">{t.name}</h3>
                  {t.description && <p className="text-xs text-[#334155] mt-1 line-clamp-2">{t.description}</p>}
                </div>
              </div>
              {isAdmin && (
                <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-[#F0EFEB]">
                  <button onClick={() => { setEditing(t); setShowForm(true); }} className="text-[11px] text-[#1E3A8A] inline-flex items-center gap-1 px-2 py-1 rounded-full hover:bg-[#F9F8F6]" data-testid={`edit-dt-${t.id}`}>
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <button onClick={() => remove(t)} className="text-[11px] text-[#991B1B] inline-flex items-center gap-1 px-2 py-1 rounded-full hover:bg-[#FEE2E2]" data-testid={`delete-dt-${t.id}`}>
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Placeholders helper */}
      <div className="bg-[#F9F8F6] border border-[#E5E5E0] rounded-2xl p-5" data-testid="placeholders-helper">
        <div className="flex items-center gap-2 mb-3">
          <Eye className="w-4 h-4 text-[#1E3A8A]" strokeWidth={1.75} />
          <h3 className="font-display text-base font-semibold text-[#0F172A]">Available placeholders</h3>
        </div>
        <p className="text-xs text-[#334155] mb-3">
          Drop these tokens anywhere in a template body — they'll be replaced with real data when staff render the document for a Care Seeker.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1.5 text-xs">
          {PLACEHOLDERS.map((p) => (
            <div key={p.token} className="flex items-center justify-between gap-2">
              <code className="font-mono text-[11px] bg-white border border-[#E5E5E0] rounded px-1.5 py-0.5 text-[#1E40AF]">{p.token}</code>
              <span className="text-[#7E937D] truncate">{p.note}</span>
            </div>
          ))}
        </div>
      </div>

      {showForm && (
        <DocumentTemplateForm
          existing={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function DocumentTemplateForm({ existing, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: existing?.name || "",
    description: existing?.description || "",
    kind: existing?.kind || "other",
    body: existing?.body || "",
    active: existing?.active !== false,
  });
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef(null);

  const insertToken = (token) => {
    const ta = bodyRef.current;
    if (!ta) {
      setForm((f) => ({ ...f, body: (f.body || "") + token }));
      return;
    }
    const start = ta.selectionStart ?? ta.value.length;
    const end = ta.selectionEnd ?? ta.value.length;
    const before = ta.value.slice(0, start);
    const after = ta.value.slice(end);
    const next = before + token + after;
    setForm((f) => ({ ...f, body: next }));
    // restore caret position right after the inserted token
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + token.length;
      try { ta.setSelectionRange(pos, pos); } catch { /* no-op */ }
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (existing) {
        await api.patch(`/document-templates/${existing.id}`, form);
        toast.success("Template updated");
      } else {
        await api.post("/document-templates", form);
        toast.success("Template created");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="dt-form">
        <div className="sticky top-0 bg-white border-b border-[#E5E5E0] px-6 py-4 flex justify-between">
          <h2 className="font-display text-xl font-semibold">{existing ? "Edit" : "New"} document template</h2>
          <button onClick={onClose} data-testid="dt-form-close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-1.5">Name *</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                data-testid="dt-name"
                placeholder="e.g., Consent for Services"
                className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-1.5">Kind</label>
              <select
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}
                className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]"
              >
                {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-1.5">Description</label>
            <input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]"
              placeholder="Short hint for staff"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-[#334155]">Body</label>
              <span className="text-[11px] text-[#7E937D]">
                Click a placeholder on the right to insert it at your cursor.
              </span>
            </div>
            <div className="grid lg:grid-cols-[1fr,280px] gap-3">
              <textarea
                ref={bodyRef}
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                rows={16}
                data-testid="dt-body"
                className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0] font-mono text-[12px]"
                placeholder={"Type the document text. Click placeholder chips on the right to insert {{tokens}} that will fill in with Care Seeker data when the PDF is generated."}
              />
              <div
                className="bg-[#F9F8F6] border border-[#E5E5E0] rounded-xl p-3 max-h-[420px] overflow-y-auto"
                data-testid="dt-placeholder-palette"
              >
                <div className="text-[11px] uppercase tracking-wider text-[#1E3A8A] font-semibold mb-2">
                  Insert placeholder
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {PLACEHOLDERS.map((p) => (
                    <button
                      key={p.token}
                      type="button"
                      onClick={() => insertToken(p.token)}
                      title={`${p.note} — click to insert at cursor`}
                      data-testid={`dt-insert-${p.token.replace(/[{}]/g, "")}`}
                      className="font-mono text-[11px] bg-white border border-[#E5E5E0] rounded px-1.5 py-0.5 text-[#1E40AF] hover:bg-[#DBEAFE] hover:border-[#1E40AF] transition-colors"
                    >
                      {p.token}
                    </button>
                  ))}
                </div>
                <div className="text-[11px] text-[#7E937D] mt-3 leading-snug">
                  Hover any chip to see what data it inserts. Tokens render
                  as plain text here — they become real values when staff
                  generate the PDF for a Care Seeker.
                </div>
              </div>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              data-testid="dt-active"
              className="rounded"
            />
            <span className="text-sm text-[#334155]">Active (visible to staff in the render menu)</span>
          </label>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-full border border-[#E5E5E0] text-[#334155] font-medium">Cancel</button>
            <button type="submit" disabled={saving} data-testid="dt-submit" className="flex-1 py-3 rounded-full bg-[#1E3A8A] text-white font-medium disabled:opacity-60">
              {saving ? "Saving…" : existing ? "Save changes" : "Create template"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
